import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const IslandVisibilityToggleDialog = ({
  isOpen,
  onClose,
  onConfirm,
  islandId,
  isPublicVisible,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  islandId: string;
  isPublicVisible: boolean;
}) => {
  const { updateIslandVisibility } = useIotsApi();
  const updateIslandVisibilityMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => updateIslandVisibility(id),
  });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      onConfirm={() => {
        updateIslandVisibilityMutation.mutate(
          { id: islandId },
          {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.island-update-success'),
              });
              onConfirm();
              onClose();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.island-update-error'),
                ),
              });
              updateIslandVisibilityMutation.reset();
            },
          },
        );
      }}
      loading={updateIslandVisibilityMutation.isPending}
      title={t(
        `content.island-visibility-confirmation-dialog.${isPublicVisible ? 'hide' : 'show'}.title`,
      )}
    >
      <ConsequencesList
        action={t(
          `content.island-visibility-confirmation-dialog.${isPublicVisible ? 'hide' : 'show'}.action`,
        )}
        list={[
          t(
            `content.island-visibility-confirmation-dialog.${isPublicVisible ? 'hide' : 'show'}.bullet-points.explanation`,
          ),
        ]}
      />
    </ConfirmationDialog>
  );
};

export default IslandVisibilityToggleDialog;
